import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ChartBarIcon, 
  EyeIcon, 
  CalendarIcon, 
  UserIcon,
  ArrowRightIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';
import { testApi } from '../services/api';

function Results() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailedResults, setDetailedResults] = useState(null);

  useEffect(() => {
    // Buscar la información de la sesión activa guardada en localStorage
    const activeParticipantStr = localStorage.getItem('activeParticipant');
    
    // Si la sesión existe, cargar los datos específicos del participante
    if (activeParticipantStr) {
      // Parsear la cadena JSON a un objeto JavaScript
      const activeParticipant = JSON.parse(activeParticipantStr);
      // Consultar los resultados pasando el ID dinámico de la sesión
      loadSessions(activeParticipant.id);
    } else {
      // Redirigir a la pantalla de login si no hay sesión iniciada
      navigate('/login');
    }
  }, [navigate]);

  const loadSessions = async (participantId) => {
    try {
      setLoading(true);
      // Consultar al backend los resultados históricos correspondientes al ID real del participante
      const response = await testApi.getParticipantResults(participantId);
      setSessions(response.data);
    } catch (error) {
      toast.error('Error al cargar los resultados');
      console.error('Results load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionDetails = async (sessionId) => {
    try {
      const response = await testApi.getSessionResults(sessionId);
      const raw = response.data;

      // Normalizar la respuesta plana del backend al formato que espera el renderizado.
      // El backend devuelve un objeto plano; aquí lo transformamos en objetos anidados.
      const normalized = {
        session_date: raw.session_date,

        // Datos del test MBTI agrupados en su propio objeto
        mbti: raw.personality_type ? {
          personality_type: raw.personality_type,          // Tipo: ISFP, ENTJ, etc.
          percentage_score: raw.mbti_percentage,           // Porcentaje de madurez
          description: raw.mbti_description,               // Descripción textual del tipo
          strengths: raw.strengths,                        // Listado de fortalezas
          weaknesses: raw.weaknesses,                      // Listado de debilidades
          scores: raw.scores,                              // Puntuaciones E/I, S/N, T/F, J/P
        } : null,

        // Datos del test VIA agrupados en su propio objeto
        via: raw.top_strengths ? {
          top_strengths: raw.top_strengths,
          percentage_score: raw.via_percentage,
          description: raw.via_description,
        } : null,

        // Datos del test Eneagrama agrupados en su propio objeto
        enneagram: raw.primary_type ? {
          primary_type: raw.primary_type,
          wing_types: raw.wing_types,
          percentage_score: raw.enneagram_percentage,
          description: raw.enneagram_description,
        } : null,

        // Normalizar los pilares: la API devuelve { pillar, percentage, level, evidence }
        // pero el renderizado espera { pillar_name, percentage_score, maturity_level, evidence_notes }
        maturity_scores: Array.isArray(raw.maturity_scores)
          ? raw.maturity_scores
              .filter(s => s.pillar !== null) // Filtrar filas vacías del LEFT JOIN
              .map(s => ({
                pillar_name: s.pillar,           // Renombrar campo
                percentage_score: s.percentage,  // Renombrar campo
                maturity_level: s.level,         // Renombrar campo
                evidence_notes: s.evidence,      // Renombrar campo
              }))
          : [],
      };

      setDetailedResults(normalized);
    } catch (error) {
      toast.error('Error al cargar los detalles de la sesión');
      console.error('Session details error:', error);
    }
  };

  const getMaturityLevelColor = (score) => {
    if (score >= 76) return 'maturity-76-100';
    if (score >= 51) return 'maturity-51-75';
    if (score >= 26) return 'maturity-26-50';
    return 'maturity-1-25';
  };

  // Devuelve la etiqueta textual del nivel de madurez según el puntaje
  const getMaturityLevelText = (score) => {
    if (score >= 76) return 'Optimizado / Mentor';
    if (score >= 51) return 'Competente / Consistente';
    if (score >= 26) return 'En Desarrollo';
    return 'Inicial / Reactivo';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Resultados de Evaluaciones</h1>
        <button
          onClick={() => navigate('/tests')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center"
        >
          Realizar Nuevo Test
          <ArrowRightIcon className="h-4 w-4 ml-2" />
        </button>
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-lg card-shadow">
        <h2 className="text-xl font-bold text-gray-900 p-6 pb-4">Historial de Sesiones</h2>
        
        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <EyeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes resultados aún</h3>
            <p className="text-gray-600 mb-4">
              Realiza tu primera evaluación para ver tus resultados aquí
            </p>
            <button
              onClick={() => navigate('/tests')}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Comenzar Evaluación
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sessions.map((session) => (
              <div key={session.session_id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-500">
                        {new Date(session.session_date).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                      {session.tests.mbti && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center mb-2">
                            <UserIcon className="h-4 w-4 text-indigo-600 mr-2" />
                            <span className="text-sm font-medium text-gray-900">MBTI</span>
                          </div>
                          <p className="text-sm text-gray-600">{session.tests.mbti.personality_type}</p>
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>Madurez</span>
                              <span>{session.tests.mbti.percentage_score}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getMaturityLevelColor(session.tests.mbti.percentage_score)}`}
                                style={{ width: `${session.tests.mbti.percentage_score}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {session.tests.via && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center mb-2">
                            <ChartBarIcon className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-sm font-medium text-gray-900">VIA</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {session.tests.via.top_strengths?.[0] || 'Fortaleza principal'}
                          </p>
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>Madurez</span>
                              <span>{session.tests.via.percentage_score}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getMaturityLevelColor(session.tests.via.percentage_score)}`}
                                style={{ width: `${session.tests.via.percentage_score}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {session.tests.enneagram && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center mb-2">
                            <ClockIcon className="h-4 w-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium text-gray-900">Eneagrama</span>
                          </div>
                          <p className="text-sm text-gray-600">Tipo {session.tests.enneagram.primary_type}</p>
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>Madurez</span>
                              <span>{session.tests.enneagram.percentage_score}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getMaturityLevelColor(session.tests.enneagram.percentage_score)}`}
                                style={{ width: `${session.tests.enneagram.percentage_score}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => loadSessionDetails(session.session_id)}
                    className="ml-4 px-4 py-2 text-indigo-600 hover:text-indigo-500 font-medium text-sm"
                  >
                    Ver Detalles
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detailed Results */}
      {detailedResults && (
        <div className="bg-white rounded-lg card-shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Detalles de la Sesión - {new Date(detailedResults.session_date).toLocaleDateString('es-ES')}
              </h2>
              <button
                onClick={() => setDetailedResults(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                Cerrar
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-8">
            {/* MBTI Results */}
            {detailedResults.mbti && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">16 Personalidades (MBTI)</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Tipo de Personalidad</p>
                    <p className="text-2xl font-bold text-indigo-600">{detailedResults.mbti.personality_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Nivel de Madurez</p>
                    <div className="flex items-center">
                      <div className={`flex-1 bg-gray-200 rounded-full h-3 ${getMaturityLevelColor(detailedResults.mbti.percentage_score)}`}>
                        <div 
                          className="bg-indigo-600 h-3 rounded-full progress-bar"
                          style={{ width: `${detailedResults.mbti.percentage_score}%` }}
                        ></div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {detailedResults.mbti.percentage_score}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {getMaturityLevelText(detailedResults.mbti.percentage_score)}
                    </p>
                  </div>
                </div>
                {detailedResults.mbti.description && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">Descripción</p>
                    <p className="text-gray-900">{detailedResults.mbti.description}</p>
                  </div>
                )}
                {detailedResults.mbti.strengths && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">Fortalezas</p>
                    <ul className="list-disc list-inside text-gray-900">
                      {detailedResults.mbti.strengths.map((strength, index) => (
                        <li key={index}>{strength}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Debilidades del tipo MBTI */}
                {detailedResults.mbti.weaknesses && detailedResults.mbti.weaknesses.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">Áreas de mejora</p>
                    <ul className="list-disc list-inside text-gray-900">
                      {detailedResults.mbti.weaknesses.map((weakness, index) => (
                        <li key={index}>{weakness}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Dimensiones E/I, S/N, T/F, J/P como barras de proporción */}
                {detailedResults.mbti.scores && (
                  <div className="mt-6">
                    <p className="text-sm text-gray-600 mb-3">Dimensiones de Personalidad</p>
                    <div className="space-y-3">
                      {/* Par E vs I */}
                      {['E','I','S','N','T','F','J','P']
                        .reduce((pairs, _, i, arr) => i % 2 === 0 ? [...pairs, [arr[i], arr[i+1]]] : pairs, [])
                        .map(([left, right]) => {
                          // Total de votos en este eje para calcular porcentaje relativo
                          const total = (detailedResults.mbti.scores[left] || 0) + (detailedResults.mbti.scores[right] || 0);
                          const leftPct = total > 0 ? Math.round((detailedResults.mbti.scores[left] / total) * 100) : 50;
                          const rightPct = 100 - leftPct;
                          return (
                            <div key={left}>
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>{left} ({detailedResults.mbti.scores[left]})</span>
                                <span>{right} ({detailedResults.mbti.scores[right]})</span>
                              </div>
                              {/* Barra bicolor mostrando proporción de cada polo */}
                              <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
                                <div className="bg-indigo-500 h-2" style={{ width: `${leftPct}%` }}></div>
                                <div className="bg-purple-400 h-2" style={{ width: `${rightPct}%` }}></div>
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VIA Results */}
            {detailedResults.via && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Fortalezas de Carácter (VIA)</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Top Fortalezas</p>
                    <ul className="space-y-1">
                      {detailedResults.via.top_strengths?.map((strength, index) => (
                        <li key={index} className="text-gray-900">• {strength}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Nivel de Madurez</p>
                    <div className="flex items-center">
                      <div className={`flex-1 bg-gray-200 rounded-full h-3 ${getMaturityLevelColor(detailedResults.via.percentage_score)}`}>
                        <div 
                          className="bg-purple-600 h-3 rounded-full progress-bar"
                          style={{ width: `${detailedResults.via.percentage_score}%` }}
                        ></div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {detailedResults.via.percentage_score}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {getMaturityLevelText(detailedResults.via.percentage_score)}
                    </p>
                  </div>
                </div>
                {detailedResults.via.description && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">Descripción</p>
                    <p className="text-gray-900">{detailedResults.via.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* Enneagram Results */}
            {detailedResults.enneagram && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Eneagrama</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Tipo Principal</p>
                    <p className="text-2xl font-bold text-green-600">Tipo {detailedResults.enneagram.primary_type}</p>
                    {detailedResults.enneagram.wing_types && (
                      <p className="text-sm text-gray-600 mt-2">
                        Alas: {detailedResults.enneagram.wing_types.join(', ')}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Nivel de Madurez</p>
                    <div className="flex items-center">
                      <div className={`flex-1 bg-gray-200 rounded-full h-3 ${getMaturityLevelColor(detailedResults.enneagram.percentage_score)}`}>
                        <div 
                          className="bg-green-600 h-3 rounded-full progress-bar"
                          style={{ width: `${detailedResults.enneagram.percentage_score}%` }}
                        ></div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {detailedResults.enneagram.percentage_score}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {getMaturityLevelText(detailedResults.enneagram.percentage_score)}
                    </p>
                  </div>
                </div>
                {detailedResults.enneagram.description && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">Descripción</p>
                    <p className="text-gray-900">{detailedResults.enneagram.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* Maturity Scores */}
            {detailedResults.maturity_scores && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pilares de Madurez</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {detailedResults.maturity_scores.map((score, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{score.pillar_name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          score.percentage_score >= 76 ? 'bg-green-100 text-green-800' :
                          score.percentage_score >= 51 ? 'bg-blue-100 text-blue-800' :
                          score.percentage_score >= 26 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {score.percentage_score}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className={`h-2 rounded-full ${getMaturityLevelColor(score.percentage_score)}`}
                          style={{ width: `${score.percentage_score}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600">
                        {getMaturityLevelText(score.percentage_score)}
                      </p>
                      {score.evidence_notes && (
                        <p className="text-xs text-gray-500 mt-2">{score.evidence_notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Results;
